import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Palette, Footprints, Table2, FolderOpen, HelpCircle, Star, ArrowUpRight, Inbox, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const COUNTS = [
  { key: "themes", label: "Temi", endpoint: "/admin/themes", icon: Palette, to: "/cms/temi" },
  { key: "models", label: "Modelli", endpoint: "/admin/models", icon: Footprints, to: "/cms/modelli" },
  { key: "prices", label: "Regole prezzo", endpoint: "/admin/price-rules", icon: Table2, to: "/cms/prezzi" },
  { key: "portfolio", label: "Portfolio", endpoint: "/admin/portfolio", icon: FolderOpen, to: "/cms/portfolio" },
  { key: "faqs", label: "FAQ", endpoint: "/admin/faqs", icon: HelpCircle, to: "/cms/faq" },
  { key: "reviews", label: "Cosa dicono i clienti", endpoint: "/admin/reviews", icon: Star, to: "/cms/recensioni" },
];

const STATUS_LABELS: Record<string, string> = {
  new: "Nuove", in_review: "In revisione", quoted: "Preventivo",
  accepted: "Accettate", rejected: "Rifiutate", done: "Completate",
};
const CATEGORY: Record<string, string> = { baby: "Baby", kids: "Kids", adulti: "Adulti" };

interface Stats { total: number; new: number; deleted: number; by_status: Record<string, number>; }
interface RecentReq { id: string; created_at: string; status: string; category: string | null; is_colors_only: boolean; contact: { full_name: string | null }; }

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short" });

export default function DashboardHome() {
  const { user } = useAuth();
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [stats, setStats] = useState<Stats | null>(null);
  const [recent, setRecent] = useState<RecentReq[]>([]);

  useEffect(() => {
    COUNTS.forEach((c) => {
      api.get(c.endpoint).then(({ data }) => setCounts((p) => ({ ...p, [c.key]: data.length }))).catch(() => {});
    });
    api.get("/admin/requests/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/admin/requests").then(({ data }) => setRecent(data.slice(0, 5))).catch(() => {});
  }, []);

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} data-testid="dashboard-home">
      <p className="text-xs uppercase tracking-[0.2em] font-bold text-secondary mb-3">Panoramica</p>
      <h1 className="font-display font-black tracking-tighter text-4xl md:text-5xl mb-2">Ciao, {user?.name}</h1>
      <p className="text-muted-foreground max-w-xl">Gestisci richieste, configuratore e contenuti del sito.</p>

      {/* Richieste — metriche */}
      <div className="grid md:grid-cols-[1fr_1.4fr] gap-4 mt-10" data-testid="dashboard-requests">
        <Link to="/cms/richieste" data-testid="dash-new-requests"
          className="group relative overflow-hidden border border-primary/40 bg-primary/5 p-6 hover:border-primary transition-colors">
          <div className="flex items-center justify-between mb-6">
            <Inbox className="h-6 w-6 text-primary" />
            <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
          <p className="font-display font-black text-5xl leading-none text-primary">{stats?.new ?? "—"}</p>
          <p className="text-xs uppercase tracking-[0.15em] font-bold text-muted-foreground mt-2">Nuove richieste</p>
          <p className="text-xs text-muted-foreground mt-3">Totale ricevute: <span className="text-foreground font-semibold">{stats?.total ?? "—"}</span></p>
        </Link>

        <div className="border border-white/10 bg-card p-6">
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-5">Richieste per stato</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {Object.keys(STATUS_LABELS).map((k) => (
              <div key={k} data-testid={`dash-status-${k}`}>
                <p className="font-display font-black text-2xl leading-none">{stats?.by_status?.[k] ?? 0}</p>
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground mt-1">{STATUS_LABELS[k]}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Richieste recenti */}
      {recent.length > 0 && (
        <div className="mt-4 border border-white/10 bg-card" data-testid="dashboard-recent">
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Richieste recenti</p>
            <Link to="/cms/richieste" className="text-xs uppercase tracking-wider font-bold text-muted-foreground hover:text-secondary transition-colors">Tutte →</Link>
          </div>
          <ul className="divide-y divide-white/5">
            {recent.map((r) => (
              <li key={r.id}>
                <Link to="/cms/richieste" className="flex items-center justify-between px-6 py-3 hover:bg-white/5 transition-colors">
                  <span className="flex items-center gap-3 min-w-0">
                    <span className="font-semibold text-sm truncate">{r.contact.full_name || "—"}</span>
                    <span className="text-xs text-muted-foreground shrink-0">{r.is_colors_only ? "Solo colori" : (CATEGORY[r.category || ""] || "—")}</span>
                  </span>
                  <span className="flex items-center gap-4 shrink-0">
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{STATUS_LABELS[r.status] || r.status}</span>
                    <span className="text-xs text-muted-foreground">{fmtDate(r.created_at)}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Catalogo & contenuti */}
      <div className="flex items-center gap-2 mt-12 mb-4">
        <Sparkles className="h-4 w-4 text-secondary" />
        <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Catalogo & contenuti</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {COUNTS.map((c) => {
          const Icon = c.icon;
          return (
            <Link key={c.key} to={c.to} data-testid={`dash-card-${c.key}`}
              className="group border border-white/10 bg-card p-6 hover:border-primary transition-colors">
              <div className="flex items-center justify-between mb-8">
                <Icon className="h-6 w-6 text-primary" />
                <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-secondary transition-colors" />
              </div>
              <p className="font-display font-black text-4xl leading-none">{counts[c.key] ?? "—"}</p>
              <p className="text-xs uppercase tracking-[0.15em] font-bold text-muted-foreground mt-2">{c.label}</p>
            </Link>
          );
        })}
      </div>
    </motion.div>
  );
}
